import {
  Add,
  DoNotDisturb,
  Edit,
  Delete,
  SettingsBackupRestore,
  ViewList,
  History,
} from '@mui/icons-material';
import { Alert, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import { getCurrentUserAssociation } from '../../../modules/project';
import {
  deleteTestSuite,
  getTestSuitesWithMetrics,
  handleActivateText,
  handleSuspendText,
  suspendTestSuite,
} from '../../../modules/testsuites';
import { testSuiteTableHeader } from '../../../static-data/users';
import ConfirmationModal from '../../elements/ConfirmationModal';
import SearchBar from '../../elements/SearchBar';
import TableComponent from '../../elements/TableComponent';
import TestSuiteTableAction from './TestSuiteTableAction';
import ViewTestCaseModal from './ViewTestCaseModal';
import CustomIconButton from '../../elements/CustomIconButton';

const MODAL_ACTION = {
  STATUS_CHANGE: 'STATUS_CHANGE',
  PERMANENT_DELETE: 'PERMANENT_DELETE',
};
const TestSuitesTabContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testSuites, setSuites] = useState([]);
  const [order, setOrder] = useState('asc');
  const { projectId } = useParams();
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [modalAction, setModalAction] = useState(MODAL_ACTION.STATUS_CHANGE);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [mode, setMode] = useState('add');
  const { addNotification } = useNotification();
  const [viewTestCase, setViewTestCase] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchQuery = useDebounce(searchValue, 800);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handleDeleteClick = (suite) => {
    setOpenDelete(true);
    setModalAction(MODAL_ACTION.STATUS_CHANGE);
    setSelectedSuite(suite);
  };

  const handlePermanentDeleteClick = (suite) => {
    setOpenDelete(true);
    setModalAction(MODAL_ACTION.PERMANENT_DELETE);
    setSelectedSuite(suite);
  };

  const handleEditClick = (suite) => {
    setOpenAdd(true);
    setSelectedSuite(suite);
    setMode('edit');
  };

  const handleDeleteCancel = () => {
    setOpenDelete(false);
    setModalAction(MODAL_ACTION.STATUS_CHANGE);
    setSelectedSuite(null);
  };

  const handleViewClick = (suite) => {
    setViewTestCase(true);
    setSelectedSuite(suite);
  };

  const handleViewClose = () => {
    setViewTestCase(false);
    setSelectedSuite(null);
  };

  const handleSearch = (value) => {
    setLoading(true);
    setSearchValue(value);
    setPage(1);
  };

  const fetchCurrentUserData = async () => {
    try {
      const response = await getCurrentUserAssociation(projectId);
      if (
        (response && response.role === 'admin') ||
        response.role === 'org-admin' ||
        response.role === 'org-manager'
      ) {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTestSuitesData = async () => {
    setLoading(true);
    try {
      const response = await getTestSuitesWithMetrics(
        projectId,
        page,
        perPage,
        searchValue
      );
      setSuites(response.data);
      setTotalCount(response?.total);
      setLoading(false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUserData();
  }, []);

  useEffect(() => {
    fetchTestSuitesData();
  }, [projectId, debouncedSearchQuery, page, perPage]);

  const handleDelete = async () => {
    try {
      if (modalAction === MODAL_ACTION.PERMANENT_DELETE) {
        await deleteTestSuite(selectedSuite.id);

        if (selectedSuite?.test_case_count > 0) {
          throw new Error(
            "This suite can't be deleted as it contains test cases."
          );
        }

        addNotification(
          'success',
          `Test suite "${selectedSuite.name}" permanently deleted.`
        );
      } else {
        const status = selectedSuite?.status === 'suspended' ? 'active' : 'suspended';
        await suspendTestSuite({ status: status, id: selectedSuite.id });
        addNotification(
          'success',
          `Test suite successfully ${selectedSuite?.status === 'suspended'
            ? 'activated'
            : 'marked as suspended'
          }.`
        );
      }
      fetchTestSuitesData();
      handleDeleteCancel();
    } catch (error) {
      addNotification(
        'error',
        error.message || 'Failed to update test suite. Please try again.'
      );
      handleDeleteCancel();
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <>
      <Container fluid>
        <Row className="align-items-center">
          <Col>
            <Typography variant="h4" className="mb-3">
              Test Suites
            </Typography>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col md="6">
            <SearchBar
              placeholder="Search test suite(s)"
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              clearSearch={() => handleSearch('')}
            />
          </Col>
          {isAdmin && (
            <Col className="d-flex justify-content-end">
              <Button variant="primary" onClick={() => setOpenAdd(true)}>
                <Add /> New Test Suite
              </Button>
            </Col>
          )}
        </Row>
        <Row>
          {!loading && (
            <div className="mt-3">
              {searchValue === '' && testSuites?.length === 0 ? (
                isAdmin ? (
                  <Alert severity="warning">
                    No test suites found. Please create a test suite to add test
                    cases, as they are required for adding test cases.
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    No test suites found. Please ask your project admin to
                    create test suites, as they are required for adding test
                    cases.
                  </Alert>
                )
              ) : null}
            </div>
          )}
          <Col>
            <TableComponent
              data={testSuites || []}
              headerTabs={testSuiteTableHeader}
              page={page}
              setPage={setPage}
              perPage={perPage}
              order={order}
              setOrder={setOrder}
              defaultSort="latest_execution_date"
              isLoading={loading}
              striped
              totalCount={totalCount}
              handlePageChange={handlePageChange}
              showPages
              tablekey="test suites"
              showCount={true}
              showRowCount={true}
              setPerPage={handlePageSizeChange}
              action={(row) => (
                <>
                  <CustomIconButton
                    title="Manage Test Cases"
                    onClick={() => handleViewClick(row)}>
                    <ViewList sx={{ color: 'var(--primary-color)' }} />
                  </CustomIconButton>
                  {isAdmin ? (
                    <>
                      {row.status === 'suspended' ? (
                        <>
                          <CustomIconButton
                            title="Restore Test Suite"
                            onClick={() => handleDeleteClick(row)}>
                            <SettingsBackupRestore sx={{ color: 'green' }} />
                          </CustomIconButton>
                          <CustomIconButton
                            title="Permanently Delete Test Suite"
                            onClick={() => handlePermanentDeleteClick(row)}>
                            <Delete sx={{ color: 'darkred' }} />
                          </CustomIconButton>
                        </>
                      ) : (
                        <>
                          <CustomIconButton
                            title="Edit Test Suite"
                            onClick={() => handleEditClick(row)}>
                            <Edit sx={{ color: 'var(--secondary-color)' }} />
                          </CustomIconButton>
                          <CustomIconButton
                            title="Suspend Test Suite"
                            onClick={() => handleDeleteClick(row)}>
                            <DoNotDisturb sx={{ color: 'red' }} />
                          </CustomIconButton>
                        </>
                      )}
                    </>
                  ) : null}
                  <Tooltip title="View History">
                    <IconButton
                      onClick={() => navigate(`/project/${projectId}/testsuite/${row.id}/history`)}
                      disableRipple
                      aria-label="View History">
                      <History />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            />
          </Col>
        </Row>
        <ConfirmationModal
          show={openDelete}
          handleConfirm={handleDelete}
          handleClose={handleDeleteCancel}
          title={
            modalAction === MODAL_ACTION.PERMANENT_DELETE
              ? 'Permanently Delete Test Suite'
              : selectedSuite?.status === 'suspended'
              ? 'Restore Test Suite'
              : 'Suspend Test Suite'
          }
          body={
            modalAction === MODAL_ACTION.PERMANENT_DELETE
              ? `Are you sure you want to permanently delete the test suite: ${selectedSuite?.name}? This action is irreversible.`
              : selectedSuite?.status === 'suspended'
              ? handleActivateText
              : handleSuspendText
          }
          confirmButtonVariant={
            modalAction === MODAL_ACTION.PERMANENT_DELETE ? 'danger' : 'primary'
          }
        />
        <TestSuiteTableAction
          fetchTestSuitesData={fetchTestSuitesData}
          setOpenModal={setOpenAdd}
          open={openAdd}
          mode={mode}
          setMode={setMode}
          selectedSuite={selectedSuite}
          setSelectedSuite={setSelectedSuite}
        />
      </Container>
      <ViewTestCaseModal
        show={viewTestCase}
        handleClose={handleViewClose}
        selectedSuite={selectedSuite}
        fetchTestSuitesData={fetchTestSuitesData}
      />
    </>
  );
};

export default TestSuitesTabContent;
